import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createUserSubscription,
  deleteSubscription,
  getSubscriptionPlansList,
  getSubscriptions,
  updateUserSubscription,
} from "../../../services/api/subscriptions/subscriptionsApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

const SUBSCRIPTION_ADD_ERROR_FALLBACK = {
  en: "Failed to add subscription.",
  ar: "فشل في إضافة الاشتراك.",
};

const SUBSCRIPTION_UPDATE_ERROR_FALLBACK = {
  en: "Failed to update subscription.",
  ar: "فشل في تحديث الاشتراك.",
};

export const fetchSubscriptions = createAsyncThunk(
  "subscriptions/fetchSubscriptions",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getSubscriptions(dateRange);
      return {
        subscriptions: response.data.subscriptions || [],
        total_subscriptions: response.data.total_subscriptions,
        plans_breakdown: response.data.plans_breakdown,
      };
    } catch (error) {
      return rejectWithValue("Failed to fetch subscriptions");
    }
  },
);

export const fetchSubscriptionCounts = createAsyncThunk(
  "subscriptions/fetchSubscriptionCounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSubscriptionPlansList();
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch subscription counts");
    }
  },
);

export const cancelSubscription = createAsyncThunk(
  "subscriptions/cancelSubscription",
  async (subscriptionId, { rejectWithValue }) => {
    try {
      const response = await deleteSubscription(subscriptionId);
      if (response.data.success) {
        return subscriptionId;
      }
      return rejectWithValue(
        response.data.message || "Failed to cancel subscription",
      );
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel subscription",
      );
    }
  },
);

export const addSubscription = createAsyncThunk(
  "subscriptions/addSubscription",
  async (
    {
      userId,
      planId,
      billingCycle,
      status,
      startDate,
      endDate,
      total_signature,
      used_signature,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await createUserSubscription(userId, {
        plan_id: planId,
        cycle: billingCycle,
        status,
        start_date: startDate || null,
        end_date: endDate || null,
        user_id: userId,
        ...(total_signature !== undefined && { total_signature }),
        ...(used_signature !== undefined && { used_signature }),
      });

      const apiError = rejectApiResponse(
        response.data,
        SUBSCRIPTION_ADD_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        subscription: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, SUBSCRIPTION_ADD_ERROR_FALLBACK),
      );
    }
  },
);

export const updateSubscription = createAsyncThunk(
  "subscriptions/updateSubscription",
  async (
    {
      subscriptionId,
      userId,
      planId,
      startDate,
      endDate,
      status,
      billingCycle,
      total_signature,
      used_signature,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateUserSubscription(subscriptionId, {
        plan_id: planId,
        start_date: startDate || null,
        end_date: endDate || null,
        status,
        user_id: userId,
        ...(billingCycle !== undefined && { cycle: billingCycle }),
        ...(total_signature !== undefined && { total_signature }),
        ...(used_signature !== undefined && { used_signature }),
      });

      const apiError = rejectApiResponse(
        response.data,
        SUBSCRIPTION_UPDATE_ERROR_FALLBACK,
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        subscription: response.data?.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, SUBSCRIPTION_UPDATE_ERROR_FALLBACK),
      );
    }
  },
);

const subscriptionsSlice = createSlice({
  name: "subscriptions",
  initialState: {
    subscriptionData: [],
    subscriptionCounts: {
      total_subscriptions: 0,
      plans_breakdown: {
        Basic: 0,
        Professional: 0,
        Enhanced: 0,
      },
    },
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptionData = action.payload.subscriptions;
        state.subscriptionCounts.total_subscriptions =
          action.payload.total_subscriptions;
        state.subscriptionCounts.plans_breakdown =
          action.payload.plans_breakdown;
      })
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSubscriptionCounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionCounts.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptionCounts = action.payload;
      })
      .addCase(fetchSubscriptionCounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.subscriptionData = state.subscriptionData.filter(
          (subscription) => subscription.id !== action.payload,
        );
        const lang =
          window.localStorage.getItem("language") === "ar" ? "ar" : "en";
        toast.success(
          lang === "ar"
            ? "تم إلغاء الاشتراك بنجاح"
            : "Subscription canceled successfully",
        );
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.error = action.payload;
        const lang =
          window.localStorage.getItem("language") === "ar" ? "ar" : "en";
        let msg = action.payload;
        if (lang === "ar") {
          msg =
            typeof msg === "string" && msg.includes("Failed")
              ? "فشل في إلغاء الاشتراك"
              : msg;
        }
        toast.error(
          msg ||
            (lang === "ar"
              ? "فشل في إلغاء الاشتراك"
              : "Failed to cancel subscription"),
        );
      })
      .addCase(addSubscription.fulfilled, (state, action) => {
        if (action.payload?.subscription) {
          state.subscriptionData.push(action.payload.subscription);
        }
      })
      .addCase(addSubscription.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        state.loading = false;
        const updatedSubscription = action.payload?.subscription;
        if (!updatedSubscription) return;

        state.subscriptionData = state.subscriptionData.map((subscription) =>
          subscription.id === updatedSubscription.id
            ? updatedSubscription
            : subscription,
        );
      })
      .addCase(updateSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default subscriptionsSlice.reducer;
